import { NavigateFunction } from 'react-router';
import { BackgroundProcess } from 'src/data-capture/hooks/file-uploads/types';
import { EmailDocumentType } from 'src/meeting-note/hooks/useGenerateEmailContent';
import { Flags } from 'src/feature-flags/FlagsHook';
import { Action, ActionItem } from '../files-table/FilesTable';
import { GeneratedDocumentType } from './generatedDocumentTypes';

type GeneratedDocMenuDoc = {
  id: string;
  documentType: GeneratedDocumentType;
  fileId?: number;
  documentHistoryId?: string;
  generatedFromAgenda?: boolean;
  backgroundProcess?: BackgroundProcess;
};

type GeneratedDocMenuFlags = Pick<
  Flags,
  'flagEnabledDocumentEdit' | 'flagEnabledMeetingNoteEdit' | 'flagEnabledAgendaMeetingNoteReadOnly'
>;

type BuildGeneratedDocActionsParams = {
  doc: GeneratedDocMenuDoc;
  clientId: string;
  navigate: NavigateFunction;
  flags: GeneratedDocMenuFlags;
  includeEdit?: boolean;
  onEmail?: () => void;
  onSelect?: () => void;
  onDelete: () => void;
};

export function buildGeneratedDocActions({
  doc,
  clientId,
  navigate,
  flags,
  includeEdit = true,
  onEmail,
  onSelect,
  onDelete,
}: BuildGeneratedDocActionsParams): Action<unknown>[] {
  const actions: Action<unknown>[] = [];

  if (includeEdit) {
    const editAction = buildEditAction(doc, clientId, navigate, flags);
    if (editAction) actions.push(editAction);
  }

  const emailReady = (doc.backgroundProcess?.status ?? 'SUCCESS') === 'SUCCESS';
  if (onEmail && emailTypeFor(doc) && emailReady) {
    actions.push({ label: 'Email', action: () => onEmail() });
  }

  if (onSelect) {
    actions.push({ label: 'Select', action: onSelect });
  }

  actions.push({ label: 'Delete', color: 'red', action: onDelete });

  return actions;
}

function editRouteFor(doc: GeneratedDocMenuDoc, clientId: string): string {
  const base = `/client/${clientId}/document`;
  switch (doc.documentType) {
    case 'suitability-report':
      return `${base}/suitability-reports/${doc.id}/edit`;
    case 'meeting-agenda':
      return `${base}/meeting-agendas/${doc.id}/edit`;
    case 'data-export':
      return `${base}/data-exports/${doc.id}/edit`;
    case 'meeting-note':
      return `${base}/meeting-notes/${doc.id}/edit`;
  }
}

export function emailTypeFor(
  doc: Pick<GeneratedDocMenuDoc, 'documentType'>,
): EmailDocumentType | undefined {
  switch (doc.documentType) {
    case 'meeting-note':
      return 'MEETING_NOTE';
    case 'meeting-agenda':
      return 'MEETING_AGENDA';
    default:
      return undefined;
  }
}

function buildEditAction(
  doc: GeneratedDocMenuDoc,
  clientId: string,
  navigate: NavigateFunction,
  flags: GeneratedDocMenuFlags,
): ActionItem<unknown> | null {
  const route = editRouteFor(doc, clientId);

  if (doc.documentType === 'suitability-report') {
    if (!flags.flagEnabledDocumentEdit) return null;
    return editAction(route, navigate);
  }

  if (doc.documentType === 'meeting-agenda' || doc.documentType === 'data-export') {
    if (!flags.flagEnabledDocumentEdit || !doc.documentHistoryId) return null;
    return editAction(route, navigate);
  }

  const canEdit =
    flags.flagEnabledMeetingNoteEdit &&
    !!doc.documentHistoryId &&
    (doc.backgroundProcess?.status ?? 'SUCCESS') === 'SUCCESS';
  if (!canEdit) return null;

  const disabledForAgenda = flags.flagEnabledAgendaMeetingNoteReadOnly && !!doc.generatedFromAgenda;
  if (disabledForAgenda) {
    return {
      label: 'Edit',
      disabled: true,
      tooltip: 'Notes generated from an agenda cannot be edited yet.',
      action: () => {},
    };
  }

  return editAction(route, navigate);
}

function editAction(route: string, navigate: NavigateFunction): ActionItem<unknown> {
  return { label: 'Edit', action: () => navigate(route) };
}
